/**
 * Health Plans templates
 */

// General template for new care plan records
export const CarePlanTemplatesNewRecordTemplate = {
  general: {
    planName: '',
    planDescription: '',
    targetConditions: [],
    duration: 12,
    durationType: 'months',
    startDuringVisit: false
  },
  visits: [],
  trackers: []
}

// Column definitions for care plans display in tables
export const carePlansColumns = [
  {
    Header: 'Plan Name',
    accessor: 'planName',
    sortable: true,
    minWidth: 180
  },
  {
    Header: 'Description',
    accessor: 'planDescription',
    sortable: false,
    minWidth: 250
  },
  {
    Header: 'Created',
    accessor: 'createdAt',
    sortable: true,
    minWidth: 120,
    Cell: ({ value }) => (value ? new Date(value).toLocaleDateString() : '-')
  },
  {
    Header: 'Duration',
    accessor: 'duration',
    sortable: true,
    minWidth: 120,
    Cell: ({ row }) => {
      const { duration, durationType } = row.original
      if (!duration || !durationType) return '-'
      return `${duration} ${durationType}`
    }
  },
  {
    Header: 'Status',
    accessor: 'status',
    sortable: true,
    minWidth: 120,
    Cell: ({ value }) => {
      let statusClass = ''
      switch (value) {
        case 'active':
          statusClass = 'bg-success text-white'
          break
        case 'inactive':
          statusClass = 'bg-danger text-white'
          break
        case 'draft':
          statusClass = 'bg-warning text-dark'
          break
        default:
          statusClass = 'bg-secondary text-white'
      }
      return <span className={`py-1 px-2 rounded ${statusClass}`}>{value}</span>
    }
  },
  {
    Header: 'Actions',
    accessor: 'actions',
    sortable: false,
    minWidth: 120
  }
]

// Columns for visits display in tables
export const visitsColumns = [
  {
    Header: 'Visit Name',
    accessor: 'name',
    sortable: true,
    minWidth: 180
  },
  {
    Header: 'Type',
    accessor: 'type',
    sortable: true,
    minWidth: 150
  },
  {
    Header: 'Frequency',
    accessor: 'frequency',
    sortable: true,
    minWidth: 120
  },
  {
    Header: 'Due Date',
    accessor: 'dueDate',
    sortable: true,
    minWidth: 120,
    Cell: ({ value }) => (value ? new Date(value).toLocaleDateString() : 'Not scheduled')
  },
  {
    Header: 'Status',
    accessor: 'status',
    sortable: true,
    minWidth: 120,
    Cell: ({ value }) => {
      let statusClass = ''
      switch (value) {
        case 'Completed':
          statusClass = 'bg-success text-white'
          break
        case 'Overdue':
          statusClass = 'bg-danger text-white'
          break
        case 'Scheduled':
          statusClass = 'bg-primary text-white'
          break
        case 'Pending':
          statusClass = 'bg-warning text-dark'
          break
        default:
          statusClass = 'bg-secondary text-white'
      }
      return <span className={`py-1 px-2 rounded ${statusClass}`}>{value}</span>
    }
  }
]

// Columns for diagnostic trackers display in tables
export const trackersColumns = [
  {
    Header: 'Tracker Name',
    accessor: 'name',
    sortable: true,
    minWidth: 180
  },
  {
    Header: 'Category',
    accessor: 'category',
    sortable: true,
    minWidth: 150
  },
  {
    Header: 'Frequency',
    accessor: 'frequency',
    sortable: true,
    minWidth: 120
  },
  {
    Header: 'Last Date',
    accessor: 'lastDate',
    sortable: true,
    minWidth: 120,
    Cell: ({ value }) => (value ? new Date(value).toLocaleDateString() : 'Never')
  },
  {
    Header: 'Next Due',
    accessor: 'nextDueDate',
    sortable: true,
    minWidth: 120,
    Cell: ({ value }) => (value ? new Date(value).toLocaleDateString() : 'Not scheduled')
  },
  {
    Header: 'Status',
    accessor: 'status',
    sortable: true,
    minWidth: 120,
    Cell: ({ value }) => {
      let statusClass = ''
      switch (value) {
        case 'Completed':
          statusClass = 'bg-success text-white'
          break
        case 'Overdue':
          statusClass = 'bg-danger text-white'
          break
        case 'Scheduled':
          statusClass = 'bg-primary text-white'
          break
        case 'Pending':
          statusClass = 'bg-warning text-dark'
          break
        default:
          statusClass = 'bg-secondary text-white'
      }
      return <span className={`py-1 px-2 rounded ${statusClass}`}>{value}</span>
    }
  }
]

// Standard visit frequencies for all plan types
export const VISIT_FREQUENCIES = [
  { value: 'once', label: 'Once Only' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 Weeks' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Every 3 Months' },
  { value: 'biannually', label: 'Every 6 Months' },
  { value: 'annually', label: 'Yearly' },
  { value: 'custom', label: 'Custom' }
]

// Standard visit types
export const VISIT_TYPES = [
  { value: 'followup', label: 'Follow-up Visit' },
  { value: 'consultation', label: 'Consultation' },
  { value: 'assessment', label: 'Assessment' },
  { value: 'procedure', label: 'Procedure' },
  { value: 'other', label: 'Other' }
]

// Standard tracker categories
export const TRACKER_CATEGORIES = [
  { value: 'vitals', label: 'Vital Signs' },
  { value: 'lab', label: 'Laboratory Tests' },
  { value: 'imaging', label: 'Imaging' },
  { value: 'medication', label: 'Medication' },
  { value: 'lifestyle', label: 'Lifestyle' },
  { value: 'other', label: 'Other' }
]

export default {
  CarePlanTemplatesNewRecordTemplate,
  carePlansColumns,
  visitsColumns,
  trackersColumns,
  VISIT_FREQUENCIES,
  VISIT_TYPES,
  TRACKER_CATEGORIES
}
